// Pure offline-sync orchestration for the engineer's outbound messages. No IndexedDB, no fetch,
// no DOM -- the store is injected (the idb adapter implements it) and the network call is injected
// as `send`. Mirrors sync.ts (job completions), so the risky "drain the outbox without losing an
// engineer's message" logic is fully unit-testable in node.
//
// Session AFF fixes two defects in the original drain:
//   1. RETRY FOREVER. The old classify kept ANY non-2xx item indefinitely, so a permanently-failing
//      message (e.g. a 400 "no office recipient right now") retried on every reconnect,
//      visibilitychange, SW ping and new send -- forever. This now mirrors the completion outbox's
//      ESCALATION: a structural (4xx) failure that keeps happening, spaced out, stops retrying after
//      the same threshold and surfaces in the UI for the engineer to Retry or Remove.
//   2. DUPLICATES. A POST that succeeded server-side with a lost response was re-sent, creating a
//      duplicate message. Each queued item now carries a clientKey (stamped at enqueue) that the
//      server dedupes on, so a lost-response retry returns the existing row instead of writing again.
//
// The escalation machinery (threshold, spacing, structural allow-list) is REUSED verbatim from
// sync.ts so the two outboxes behave identically -- the constants are imported, not re-declared.
import {
  errorMessage,
  ESCALATE_AFTER_STRUCTURAL_FAILURES,
  MIN_STRUCTURAL_GAP_MS,
  MAX_SYNC_ERROR_CHARS,
} from "./sync";
import { presentableServerMessage } from "../serverMessage";

// A reply the engineer composed, queued for send. `id` is a generated key (many per thread). No
// recipient is stored: the server resolves the office recipient for an engineer sender, so an
// offline reply carries only the body (and an optional linked job).
//
// The escalation fields mirror the completion outbox's OutboxItem. Every one is OPTIONAL, so a
// reply already sitting in a device's IndexedDB from before Session AFF loads unchanged (no store
// version bump, nothing to migrate). `clientKey` is likewise optional: a pre-AFF queued reply has
// none and simply sends without server-side idempotency (harmless -- the old duplicate window).
export interface QueuedMessage {
  id: string;
  body: string;
  jobId?: string;
  queuedAt: string; // ISO -- also the send order
  attempts: number;
  lastError?: string;
  // Client-supplied idempotency key (a UUID stamped at enqueue). Sent with the message; the server
  // dedupes on it so a lost-response retry never writes a second row.
  clientKey?: string;
  // Present only once escalated. An escalated reply is never sent again automatically, not even by
  // a manual syncNow: it needs the office to become reachable, not another identical attempt.
  status?: "escalated";
  escalatedAt?: string; // ISO
  // The HTTP status that escalated it. Diagnostic only: the engineer is never shown a raw code.
  escalatedStatus?: number;
  // Consecutive structural (4xx) failures. A transient failure (5xx / network) resets it.
  structuralFailures?: number;
  // When the last structural failure was COUNTED (spacing clock; see MIN_STRUCTURAL_GAP_MS).
  lastStructuralAt?: string; // ISO
}

export interface MessageOutboxStore {
  getMessageOutbox(): Promise<QueuedMessage[]>;
  putMessage(item: QueuedMessage): Promise<void>;
  deleteMessage(id: string): Promise<void>;
}

// ok -> the server accepted it (2xx), remove from the outbox. Otherwise keep + surface the error.
// `structural` marks a 4xx that no retry will fix (the message itself / no recipient): after enough
// spaced structural failures the item escalates instead of retrying forever.
export type MessageSendResult =
  | { ok: true }
  | { ok: false; error: string; status?: number; structural?: boolean };
export type MessageSendFn = (item: QueuedMessage) => Promise<MessageSendResult>;

export interface MessageFlushSummary {
  synced: number;
  failed: number;
  // Items escalated on THIS drain (moved out of the retry loop; now need the office).
  escalated: number;
  // Everything still in the outbox afterwards: the ones that will retry, plus the ones that no
  // longer will.
  remaining: number;
}

// Which statuses are STRUCTURAL for a message send: something wrong with the submission or its
// delivery that no number of retries will fix on its own.
//   400 -- the office cannot accept it. The commonest case here is "no office contact to message
//          right now" (no active office/admin user); resolvable by the office, so escalate-and-wait
//          is right, not retry-forever.
//   403 -- forbidden (a cross-tenant/pairing rejection). Never self-heals by retrying.
//   404 -- the addressed office contact no longer exists.
//   422 -- the office would not accept the content.
// Deliberately NOT structural (kept retrying): 401 (signed out -- a re-login fixes it, and the
// auth context owns that path), 408/425 (timeouts/replay), 429 (rate limit -- clears on its own),
// and every 5xx. The body is validated before it is queued, so a real 400 here is the recipient
// case, not a bad body.
const MESSAGE_STRUCTURAL_STATUSES = new Set([400, 403, 404, 422]);

export function isMessageStructuralStatus(status: number): boolean {
  return MESSAGE_STRUCTURAL_STATUSES.has(status);
}

// Map an HTTP status to a send outcome. 2xx clears the reply; a structural 4xx is kept AND marked
// so a persistent run of them escalates; anything else is kept for a plain retry. The server's own
// message (detail) is appended, bounded, so an escalated item carries something to read.
export function classifyMessageSend(status: number, detail?: string): MessageSendResult {
  if (status >= 200 && status < 300) return { ok: true };
  const trimmed = detail?.trim();
  const base = `Server responded ${status}`;
  const error = trimmed ? `${base}: ${trimmed}`.slice(0, MAX_SYNC_ERROR_CHARS) : base;
  const structural = isMessageStructuralStatus(status);
  return structural ? { ok: false, error, status, structural: true } : { ok: false, error, status };
}

// True once a reply has stopped retrying and is waiting for the office.
export function isMessageEscalated(item: QueuedMessage): boolean {
  return item.status === "escalated";
}

// Put an escalated reply back in the retry loop (the engineer's "Retry" control). Clears the
// escalation state AND the strike/spacing clock, so a retry that fails again earns a fresh set of
// spaced strikes rather than re-escalating on the first attempt.
export function clearMessageEscalation(item: QueuedMessage): QueuedMessage {
  const next = { ...item };
  delete next.status;
  delete next.escalatedAt;
  delete next.escalatedStatus;
  delete next.structuralFailures;
  delete next.lastStructuralAt;
  delete next.lastError;
  return next;
}

// Pull the server's own sentence back out of the stored error, which classifyMessageSend formats
// as "Server responded 400: <detail>". Returns null when there was no detail to keep.
function serverDetailFrom(lastError?: string): string | null {
  if (!lastError) return null;
  const m = /^Server responded \d+:\s*(.+)$/s.exec(lastError.trim());
  // The detail comes from ApiError.code (messagesStore.ts), which is prose ONLY when the body
  // carried a `message`; otherwise it is a machine slug or the HTTP status text. Showing "forbidden"
  // as the reason a message could not be delivered would break this function's own code-free
  // contract, so anything that is not a sentence falls through to the generic fragment.
  return presentableServerMessage(m?.[1]);
}

// The plain-English reason shown to the engineer for an escalated reply. Never a raw HTTP status:
// a code is not something they can act on. Returned as a sentence fragment so the UI can render
// "The office could not receive this message: <reason>".
//
// Session AGV: PREFER THE SERVER'S OWN WORDS. A status alone cannot name a cause, and guessing one
// is worse than saying less. The 400 case guessed "there is no office contact available", which is
// only one of the reasons the server sends a 400 -- an account with no engineer profile is refused
// "Choose an engineer to message.", so the engineer was told a fact about the office that was not
// true and could not act on it. The server already sends a sentence; it was being thrown away.
export function messageEscalationReason(status?: number, lastError?: string): string {
  const detail = serverDetailFrom(lastError);
  if (detail) return detail;
  if (status === 404) return "the office contact it was addressed to no longer exists.";
  if (status === 403) return "it could not be delivered to that person.";
  // 400 and 422 both mean the office would not accept it as addressed. Without the server's own
  // detail there is nothing more specific that is safe to claim.
  if (status === 400 || status === 422) return "the office could not accept it as addressed.";
  return "the office needs to look into it.";
}

// Drain the message outbox once, in the order the engineer wrote the replies (queuedAt). Never
// throws: a success is removed, a failure is kept (and may escalate), an escalated reply is
// skipped entirely (not sent, not counted as a failure) -- including by a manual syncNow, which is
// exactly the case the skip exists for. remaining == everything still in the outbox afterwards.
export async function flushMessages(
  store: MessageOutboxStore,
  send: MessageSendFn,
  hooks: { now?: () => Date } = {},
): Promise<MessageFlushSummary> {
  const now = hooks.now ?? (() => new Date());
  const items = (await store.getMessageOutbox()).slice().sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
  let synced = 0;
  let failed = 0;
  let escalated = 0;
  let alreadyEscalated = 0;

  for (const item of items) {
    if (isMessageEscalated(item)) {
      alreadyEscalated += 1;
      continue;
    }

    let result: MessageSendResult;
    try {
      result = await send(item);
    } catch (err) {
      // A thrown send is a network failure, never a structural one: there was no response.
      result = { ok: false, error: errorMessage(err) };
    }

    if (result.ok) {
      await store.deleteMessage(item.id);
      synced += 1;
      continue;
    }

    // Consecutive structural failures only, and only if SPACED OUT: a burst of drains in a few
    // seconds (reconnect + foreground + SW ping) cannot burn all the strikes, and a transient
    // failure in the middle of a 4xx run resets the count. Mirrors the completion outbox exactly.
    const at = now();
    const previous = item.structuralFailures ?? 0;
    const lastCountedAt = item.lastStructuralAt ? Date.parse(item.lastStructuralAt) : NaN;
    const spaced = Number.isNaN(lastCountedAt) || at.getTime() - lastCountedAt >= MIN_STRUCTURAL_GAP_MS;
    const counts = result.structural === true && spaced;
    const structuralFailures = result.structural ? (counts ? previous + 1 : previous) : 0;

    const next: QueuedMessage = {
      ...item,
      attempts: item.attempts + 1,
      lastError: result.error,
      structuralFailures,
      lastStructuralAt: result.structural ? (counts ? at.toISOString() : item.lastStructuralAt) : undefined,
    };

    if (result.structural && structuralFailures >= ESCALATE_AFTER_STRUCTURAL_FAILURES) {
      next.status = "escalated";
      next.escalatedAt = at.toISOString();
      next.escalatedStatus = result.status;
      await store.putMessage(next);
      escalated += 1;
      continue;
    }

    await store.putMessage(next);
    failed += 1;
  }

  return { synced, failed, escalated, remaining: failed + escalated + alreadyEscalated };
}
